import React from 'react';

import { connect, useDispatch } from 'react-redux';

import sizeMe, { SizeMeProps } from 'react-sizeme';

import { RoomLayoutStreamType } from '../../../lib/redux_types';

import { expandStreamVideo } from '../../../lib/actions/room';
import gridElements from '../../../lib/grid';
import useDebounce from '../../../lib/utils/debounce';

import Fullscreen from '../Fullscreen';
import VideoElement from '../../VideoElement';
import VideoToolbar from '../VideoToolbar';

import mapStateToProps, { ExtendedProps } from './state';


function countSecondaryVideos(props: ExtendedProps) {
  const {
    remoteVideoStreams,
    localScreenStream,
    hasVideoStream,
  } = props;

  // one per participant, excluding the selected one
  let num = Object.keys(remoteVideoStreams).length - 1;

  // add local streams, if any
  num = hasVideoStream ? num + 1 : num;
  num = localScreenStream ? num + 1 : num;

  return Math.max(num, 0);
}


function Participants(props: ExtendedProps, width: number, height: number) {
  const {
    myStreamIsSelected,
    myScreenIsSelected,
    remoteVideoStreams,
    selectedStream,
    myUserId,
    localVideoStream,
    localScreenStream,
    hasVideoStream,
  } = props;

  const dispatch = useDispatch();

  const numSecondaryVideos = countSecondaryVideos(props);
  const participantsGrid = gridElements(width, height, numSecondaryVideos);

  let idxOffset = 0;
  const myStreams = [];
  if (selectedStream && !myStreamIsSelected && hasVideoStream && myUserId) {
    const idx = idxOffset;
    idxOffset += 1;
    const setSelectedVideo = () => {
      if (myUserId) dispatch(expandStreamVideo(myUserId, 'stream'));
    };
    myStreams.push(
      <div onDoubleClick={setSelectedVideo} key={idx} style={participantsGrid[idx]}>
        <Fullscreen user={myUserId}>
          <VideoElement
            user={myUserId}
            mirrored={true}
            addVideoMutedIconOverlay={true}
            src={localVideoStream}
          />
          <VideoToolbar uid={myUserId} />
        </Fullscreen>
      </div>
    );
  }
  if (!myScreenIsSelected && localScreenStream) {
    const idx = idxOffset;
    idxOffset += 1;
    const setSelectedVideo = () => {
      if (myUserId) dispatch(expandStreamVideo(myUserId, 'screen'));
    };
    const uid = `${myUserId}_screen`;
    myStreams.push(
      <div onDoubleClick={setSelectedVideo} key={idx} style={participantsGrid[idx]}>
        <Fullscreen user={uid}>
          <VideoElement
            user={uid}
            mirrored={false}
            addVideoMutedIconOverlay={true}
            src={localScreenStream}
          />
          <VideoToolbar uid={uid} />
        </Fullscreen>
      </div>
    );
  }

  const keys = Object.keys(remoteVideoStreams);
  const participants = keys.flatMap((uid, i) => {
    const idx = i + idxOffset;
    const video = remoteVideoStreams[uid];

    if (uid === selectedStream) {
      idxOffset -= 1;
      return [];
    }

    const setSelectedVideo = () => {
      let streamType: RoomLayoutStreamType = 'stream';
      if (uid.endsWith('_screen')) {
        streamType = 'screen';
      }
      uid = uid.replace(/_screen$/, '');
      dispatch(expandStreamVideo(uid, streamType));
    };

    if (video['stream']) {
      return [
        <div onDoubleClick={setSelectedVideo} key={idx} style={participantsGrid[idx]}>
          <Fullscreen user={uid}>
            <VideoElement
              user={uid}
              mirrored={false}
              addVideoMutedIconOverlay={true}
              src={video['stream']}
            />
            <VideoToolbar uid={uid} />
          </Fullscreen>
        </div>
      ];
    }
    else {
      return [];
    }
  });
  return [...myStreams, ...participants];
}


function SecondaryVideos(props: ExtendedProps & SizeMeProps) {
  const { size } = props;

  const debouncedSize = useDebounce(size, 100);

  if (debouncedSize.width && debouncedSize.width > 0 && debouncedSize.height && debouncedSize.height > 0) {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        {Participants(props, debouncedSize.width, debouncedSize.height)}
      </div>
    );
  }
  else {
    return null;
  }
}


export default connect(mapStateToProps)(sizeMe({ monitorHeight: true })(SecondaryVideos));
